import { IUser, TStreamStatus, IUserFlag, IUserSettings, IStreamStats, IBannerMessage, TLivePlatforms, EBannerMessageType } from '@/helpers/user';
import { ISubscription, IPaymentMethod, ISubscriptionReceipts, ESubscriptionStatus } from '@/helpers/subscription';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import SocketHelper, { ESocketEvent } from '@/helpers/socket';
import { io } from 'socket.io-client';
import Vue from 'vue';
import VueSocketIOExt from 'vue-socket.io-extended';
import UtilHelper from '@/helpers/utils';
import { EModalName } from '@/helpers/modal';
import { IStreamInfo } from '@/helpers/platforms';

@Module({ name: 'user', namespaced: true })
export default class UserModule extends VuexModule {
  user: IUser = null;
  userFlags: IUserFlag = null;
  streamStatus: TStreamStatus = 'Offline';
  livePlatforms: TLivePlatforms[] = [];
  youtubeLiveVideoId: string = null;
  showStream: boolean = true;
  subscription: ISubscription = null;
  paymentMethod: IPaymentMethod = null;
  invoices: ISubscriptionReceipts[] = null;
  streamStats: IStreamStats = null;
  socketServerUrlSetup = false;
  socketServerUrl: string = process.env.MIX_SOCKET_SERVER_URL;

  setupSLIDBannerDismissed: boolean = false;
  isPreviousPageExternal: boolean = true;
  abGroup: string = null;

  bannerMessage: IBannerMessage = null;

  streamInfo: IStreamInfo = null;

  get isAuthenticated(): boolean {
    return !!this.user;
  }

  @Action
  async setUser(user: IUser) {
    this.context.commit('SET_USER', user);
  }

  @Mutation
  SET_USER(user: IUser) {
    this.user = user;
  }

  @Action
  async setStreamStatus(status: TStreamStatus) {
    this.context.commit('SET_STREAM_STATUS', status);
  }

  @Mutation
  SET_STREAM_STATUS(status: TStreamStatus) {
    this.streamStatus = status;
  }

  @Action
  async setLivePlatforms(platforms: TLivePlatforms[]) {
    this.context.commit('SET_LIVE_PLATFORMS', platforms);
  }

  @Mutation
  SET_LIVE_PLATFORMS(platforms: TLivePlatforms[]) {
    this.livePlatforms = platforms;
  }

  @Action
  async setYoutubeLiveVideoId(videoId: string) {
    this.context.commit('SET_YOUTUBE_LIVE_VIDEO_ID', videoId);
  }

  @Mutation
  SET_YOUTUBE_LIVE_VIDEO_ID(videoId: string) {
    this.youtubeLiveVideoId = videoId;
  }

  @Action
  async setUserFlags(flags: IUserFlag) {
    this.context.commit('SET_USER_FLAGS', flags);
  }

  @Mutation
  SET_USER_FLAGS(flags: IUserFlag) {
    this.userFlags = flags;
  }

  @Action
  setShowStream(value: boolean) {
    this.context.commit('SET_SHOW_STREAM', value);
  }

  @Mutation
  SET_SHOW_STREAM(value: boolean) {
    this.showStream = value;
  }

  @Action
  setUserSettings(settings: IUserSettings) {
    this.context.commit('SET_USER_SETTINGS', settings);
  }

  @Action
  updateUserSettings(settings: Partial<IUserSettings>) {
    const updatedSettings = { ...this.user.settings, ...settings };
    this.context.commit('SET_USER_SETTINGS', updatedSettings);
  }

  @Mutation
  SET_USER_SETTINGS(settings: IUserSettings) {
    this.user.settings = settings;
  }

  @Action
  setSubscription(subscription: ISubscription) {
    this.context.commit('SET_SUBSCRIPTION', subscription);
  }

  @Mutation
  SET_SUBSCRIPTION(subscription: ISubscription) {
    this.subscription = subscription;
  }

  @Action
  setPaymentMethod(paymentMethod: IPaymentMethod) {
    this.context.commit('SET_PAYMENT_METHOD', paymentMethod);
  }

  @Mutation
  SET_PAYMENT_METHOD(paymentMethod: IPaymentMethod) {
    this.paymentMethod = paymentMethod;
  }

  @Action
  async setSubscriptionInvoices(invoices: ISubscriptionReceipts[]) {
    this.context.commit('SET_SUBSCRIPTION_INVOICES', invoices);
  }

  @Mutation
  SET_SUBSCRIPTION_INVOICES(invoices: ISubscriptionReceipts[]) {
    this.invoices = invoices;
  }

  @Action
  setStreamStats(streamStats: IStreamStats) {
    this.context.commit('SET_STREAM_STATS', streamStats);
  }

  @Mutation
  SET_STREAM_STATS(streamStats: IStreamStats) {
    this.streamStats = streamStats;
  }

  @Action
  async setSocketServerUrl(socketServerUrl: string) {
    if (!socketServerUrl || this.socketServerUrlSetup) {
      return;
    }

    this.context.commit('SET_SOCKET_SERVER_URL', socketServerUrl);
    const socketOptions = SocketHelper.socketOptions;
    const socket = io(this.socketServerUrl, socketOptions);

    Vue.use(VueSocketIOExt, socket);
  }

  @Mutation
  SET_SOCKET_SERVER_URL(socketServerUrl: string) {
    this.socketServerUrl = socketServerUrl;
    this.socketServerUrlSetup = true;
  }

  @Action setSetupSLIDBannerDismissed(setupSLIDBannerDismissed: boolean) {
    this.context.commit('SET_SETUP_SLID_BANNER_DISMISSED', setupSLIDBannerDismissed);
  }

  @Mutation
  SET_SETUP_SLID_BANNER_DISMISSED(setupSLIDBannerDismissed: boolean) {
    this.setupSLIDBannerDismissed = setupSLIDBannerDismissed;
  }

  @Action
  async connectEditorSocket() {
    if (this.user && !Vue.prototype.$socket.client.connected) {
      Vue.prototype.$socket.client.io.opts.query = { accessCode: this.user.identifier };
      Vue.prototype.$socket.client.connect();

      Vue.prototype.$socket.client.on(ESocketEvent.userStreamAttempt, () => {
        if (!(
          this.subscription &&
          (this.subscription.status === ESubscriptionStatus.active ||
            UtilHelper.dateIsFuture(this.subscription.expires_at))
        ))
          Vue.prototype.$modal.show(EModalName.checkoutMini, { plan: null });
      });

      Vue.prototype.$socket.client.on(ESocketEvent.updateStreamStatus, (data) => {
        this.setStreamStatus(data.status);
      });

      Vue.prototype.$socket.client.on(ESocketEvent.streamHealthStatus, (data) => {
        this.setStreamStats(data);
      });

      Vue.prototype.$socket.client.on(ESocketEvent.platformStreamError, (data) => {
        this.setBannerMessage({
          type: EBannerMessageType.error,
          data,
        });
      });
    }
  }

  @Action
  async setUserConnectedAccounts(accounts) {
    this.context.commit('SET_USER_CONNECTED_ACCOUNTS', accounts);
  }

  @Mutation
  SET_USER_CONNECTED_ACCOUNTS(accounts) {
    this.user.connected_accounts = accounts;
  }

  @Action
  async setABGroup(group: string) {
    this.context.commit('SET_AB_GROUP', group);
  }

  @Mutation
  SET_AB_GROUP(group: string) {
    this.abGroup = group;
  }

  @Action
  async setIsPreviousPageExternal(external: boolean) {
    this.context.commit('SET_IS_PREVIOUS_PAGE_EXTERNAL', external);
  }

  @Mutation
  SET_IS_PREVIOUS_PAGE_EXTERNAL(external: boolean) {
    this.isPreviousPageExternal = external;
  }

  @Action
  setBannerMessage(bannerMessage: IBannerMessage) {
    this.context.commit('SET_BANNER_MESSAGE', bannerMessage);
  }

  @Mutation
  SET_BANNER_MESSAGE(bannerMessage: IBannerMessage) {
    this.bannerMessage = bannerMessage;
  }

  @Action
  setStreamInfo(streamInfo: IStreamInfo) {
    this.context.commit('SET_STREAM_INFO', streamInfo);
  }

  @Mutation
  SET_STREAM_INFO(streamInfo: IStreamInfo) {
    this.streamInfo = streamInfo;
  }
}
